import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd, { getWebPageSchema, getBreadcrumbSchema, getWebsiteSchema } from '@/components/feature/JsonLd';
import useScrollToTop from '@/hooks/useScrollToTop';
import { movies, categories } from '@/mocks/movies';
import { siteInfo } from '@/mocks/siteInfo';

export default function CategoryPage() {
  useScrollToTop();
  const { slug = 'movie' } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug);
  const [sortBy, setSortBy] = useState('rating');

  useEffect(() => {
    setActiveCategory(slug);
  }, [slug]);

  const category = useMemo(() => {
    return categories.find(c => c.slug === activeCategory) || categories[1];
  }, [activeCategory]);

  const filteredMovies = useMemo(() => {
    let list = movies.filter(m => m.categorySlug === activeCategory);
    if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'newest') {
      list = [...list].sort((a, b) => b.year - a.year);
    } else if (sortBy === 'views') {
      list = [...list].sort((a, b) => b.views - a.views);
    }
    return list;
  }, [activeCategory, sortBy]);

  const pageTitle = `${category.name} | 4K${category.name}高清在线观看 | ${siteInfo.name}`;
  const pageDesc = `${siteInfo.name}提供海量4K超清${category.name}资源免费在线播放。高清画质流畅不缓冲，极速更新，支持多端同步观看。`;

  return (
    <>
      <JsonLd {...getWebsiteSchema()} />
      <JsonLd {...getWebPageSchema(pageTitle, pageDesc, `${siteInfo.domain}/category/${activeCategory}`)} />
      <JsonLd {...getBreadcrumbSchema([
        { name: '首页', url: siteInfo.domain },
        { name: category.name, url: `${siteInfo.domain}/category/${activeCategory}` },
      ])} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-3">
                <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900 font-medium">{category.name}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                4K{category.name} · 高清画质在线观看
              </h1>
              <p className="text-sm text-foreground-600">
                共找到 <strong>{filteredMovies.length}</strong> 部{category.name}资源
              </p>
            </div>
          </div>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {categories.slice(1).map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all duration-200 whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50 font-medium'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {[
                  { value: 'rating', label: '评分最高' },
                  { value: 'newest', label: '最新发布' },
                  { value: 'views', label: '最受欢迎' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1 rounded-full text-xs transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900 font-medium shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {filteredMovies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group block"
                >
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2.5 bg-background-200">
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      title={`${movie.title} - 4K${movie.category}高清画质在线观看`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className="absolute top-2 left-2 flex items-center gap-1">
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded whitespace-nowrap">
                        {movie.quality}
                      </span>
                      {movie.isNew && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded whitespace-nowrap">
                          NEW
                        </span>
                      )}
                    </div>

                    <div className="absolute top-2 right-2">
                      <span className="flex items-center gap-0.5 text-xs font-medium text-background-50 bg-background-950/60 px-1.5 py-0.5 rounded">
                        <i className="ri-star-fill text-accent-400 text-[10px]"></i>
                        {movie.rating}
                      </span>
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50">
                        <i className="ri-play-fill text-xl"></i>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {movie.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                    <span>{movie.year}</span>
                    <span>|</span>
                    <span className="line-clamp-1">{movie.genre.slice(0, 2).join(' · ')}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}